import { CURRENCIES, type CategoryKind } from "@vouch/shared";
import { CheckCircle, Circle, Copy, KeyRound, Plus, Sparkles, Trash2 } from "lucide-react";
import { useState } from "react";
import { Button, Card, Input, Select, Spinner } from "../components/ui";
import { useAuth } from "../lib/auth";
import { confirm } from "../lib/confirm";
import {
  useCategories,
  useChangePassword,
  useCreateCategory,
  useDeleteCategory,
  useCreateMcpToken,
  useMcpTokens,
  useRevokeMcpToken,
  useUpdateProfile,
} from "../lib/hooks";
import { AI_MODE_OPTIONS, type AiMode, useAiModePreference } from "../lib/preferences";
import { toast } from "../lib/toast";
import { API_URL } from "../lib/api";

type Tab = "profile" | "preferences" | "categories" | "mcp";

const TABS: { key: Tab; label: string }[] = [
  { key: "profile", label: "Profile" },
  { key: "preferences", label: "Preferences" },
  { key: "categories", label: "Categories" },
  { key: "mcp", label: "MCP access" },
];

export function SettingsPage(): JSX.Element {
  const [tab, setTab] = useState<Tab>("profile");

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <h1 className="text-2xl font-bold text-brand-500">Settings</h1>

      <div className="flex gap-1 border-b border-slate-200">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition ${
              tab === t.key
                ? "border-brand-500 text-brand-600"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "profile" && <ProfileTab />}
      {tab === "preferences" && <PreferencesTab />}
      {tab === "categories" && <CategoriesTab />}
      {tab === "mcp" && <McpTab />}
    </div>
  );
}

function McpTab(): JSX.Element {
  const { data, isLoading } = useMcpTokens();
  const createToken = useCreateMcpToken();
  const revokeToken = useRevokeMcpToken();
  const [name, setName] = useState("Codex");
  const [accessLevel, setAccessLevel] = useState<"view" | "admin">("view");
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
  const [secret, setSecret] = useState<string | null>(null);
  if (isLoading) return <Spinner />;
  const scopes = data?.scopes ?? [];

  const create = async (): Promise<void> => {
    try {
      const result = await createToken.mutateAsync({ name: name.trim() || "MCP client", accessLevel, scopes: selectedScopes, expiresInDays: 90 });
      setSecret(result.secret);
      toast.success("MCP token created");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not create token");
    }
  };

  return <div className="space-y-5">
    <Card>
      <div className="flex items-center gap-2"><KeyRound size={18} className="text-blue-600" /><h2 className="font-semibold text-slate-800">Remote MCP access</h2></div>
      <p className="mt-1 text-sm text-slate-500">Connect Codex or another MCP client to your Vouch data. Secrets are shown once and stored only as hashes.</p>
      <div className="mt-4 rounded-xl bg-slate-950 p-3 text-xs text-slate-200"><p className="text-slate-400">Endpoint</p><code className="break-all">{API_URL}/mcp</code></div>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <Input label="Token name" value={name} onChange={(event) => setName(event.target.value)} />
        <Select label="Access level" value={accessLevel} onChange={(event) => setAccessLevel(event.target.value as "view" | "admin")}><option value="view">View only</option><option value="admin">Admin — create and manage records</option></Select>
      </div>
      <div className="mt-4"><p className="text-sm font-medium text-slate-700">Tool areas</p><p className="text-xs text-slate-500">Leave all unchecked to expose every area allowed by the access level.</p><div className="mt-2 flex flex-wrap gap-2">{scopes.map((scope) => { const selected = selectedScopes.includes(scope); return <button type="button" key={scope} onClick={() => setSelectedScopes((current) => selected ? current.filter((item) => item !== scope) : [...current, scope])} className={`min-h-10 rounded-full border px-3 text-sm capitalize ${selected ? "border-blue-300 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-500"}`}>{scope}</button>; })}</div></div>
      <div className="mt-4 flex justify-end"><Button disabled={createToken.isPending || !name.trim()} onClick={() => void create()}>{createToken.isPending ? "Creating…" : "Create token"}</Button></div>
    </Card>

    {secret && <Card className="border-amber-200 bg-amber-50"><h3 className="font-semibold text-amber-900">Copy this secret now</h3><p className="mt-1 text-sm text-amber-800">It cannot be shown again.</p><div className="mt-3 flex items-center gap-2 rounded-xl bg-white p-3"><code className="min-w-0 flex-1 break-all text-xs text-slate-700">{secret}</code><Button variant="ghost" onClick={() => void navigator.clipboard.writeText(secret).then(() => toast.success("Copied"))}><Copy size={16} /> Copy</Button></div><div className="mt-3 flex justify-end"><Button variant="ghost" onClick={() => setSecret(null)}>I saved it</Button></div></Card>}

    <Card>
      <h2 className="font-semibold text-slate-800">Active tokens</h2>
      {(data?.tokens ?? []).filter((token) => !token.revokedAt).length === 0 ? <p className="mt-3 text-sm text-slate-500">No active MCP tokens.</p> : <ul className="mt-3 divide-y divide-slate-100">{(data?.tokens ?? []).filter((token) => !token.revokedAt).map((token) => <li key={token.id} className="flex flex-wrap items-center justify-between gap-3 py-3"><div><p className="font-medium text-slate-800">{token.name}</p><p className="text-xs text-slate-500"><code>{token.prefix}…</code> · {token.accessLevel} · expires {new Date(token.expiresAt).toLocaleDateString()}</p><p className="mt-0.5 text-xs text-slate-400">{token.scopes.length ? token.scopes.join(", ") : "all tool areas"}{token.lastUsedAt ? ` · used ${new Date(token.lastUsedAt).toLocaleString()}` : " · never used"}</p></div><Button variant="ghost" disabled={revokeToken.isPending} onClick={() => void revokeToken.mutateAsync(token.id).then(() => toast.success("Token revoked"))}>Revoke</Button></li>)}</ul>}
    </Card>
  </div>;
}

function ProfileTab(): JSX.Element {
  const { user, refreshUser } = useAuth();
  const updateProfile = useUpdateProfile();
  const changePassword = useChangePassword();
  const [name, setName] = useState(user?.name ?? "");
  const [currentPassword, setCurrentPassword] = useState("");

  const saveName = async (): Promise<void> => {
    if (!name.trim()) return;
    try {
      await updateProfile.mutateAsync({ name: name.trim() });
      await refreshUser();
      toast.success("Display name updated");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to update");
    }
  };

  const startPasswordChange = async (): Promise<void> => {
    if (!currentPassword) {
      toast.error("Enter your current password");
      return;
    }
    const ok = await confirm({
      title: "Change password?",
      message: "We'll email you a confirmation link to set a new password.",
      confirmLabel: "Send link",
    });
    if (!ok) return;
    try {
      await changePassword.mutateAsync(currentPassword);
      setCurrentPassword("");
      toast.success("Check your email for a confirmation link");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not start password change");
    }
  };

  return (
    <div className="space-y-5">
      <Card>
        <h2 className="mb-3 font-semibold text-slate-700">Account</h2>
        <div className="space-y-3">
          <Input label="Username (can't be changed)" value={user?.username ?? ""} disabled />
          <Input label="Email" value={user?.email ?? ""} disabled />
          <Input label="Display name" value={name} onChange={(e) => setName(e.target.value)} />
          <div className="flex justify-end">
            <Button onClick={() => void saveName()} disabled={updateProfile.isPending || !name.trim()}>
              {updateProfile.isPending ? "Saving…" : "Save name"}
            </Button>
          </div>
        </div>
      </Card>

      <Card>
        <h2 className="mb-1 font-semibold text-slate-700">Change password</h2>
        <p className="mb-3 text-sm text-slate-500">
          Confirm your current password — we'll email a secure link to set a new one.
        </p>
        <div className="space-y-3">
          <Input
            label="Current password"
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
          />
          <div className="flex justify-end">
            <Button onClick={() => void startPasswordChange()} disabled={changePassword.isPending}>
              {changePassword.isPending ? "Sending…" : "Email me a reset link"}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

function PreferencesTab(): JSX.Element {
  const { user, refreshUser } = useAuth();
  const updateProfile = useUpdateProfile();
  const [aiMode, setAiMode] = useAiModePreference();

  const saveCurrency = async (currency: string): Promise<void> => {
    try {
      await updateProfile.mutateAsync({ defaultCurrency: currency });
      await refreshUser();
      toast.success("Default currency updated");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed");
    }
  };

  return (
    <div className="space-y-5">
      <Card>
        <div className="mb-3 flex items-center gap-2">
          <Sparkles size={18} className="text-brand-500" />
          <h2 className="font-semibold text-slate-700">AI Assistant permissions</h2>
        </div>
        <p className="mb-3 text-sm text-slate-500">
          Controls what the assistant may do with your ledger. This matches the Android app.
        </p>
        <div className="space-y-2">
          {AI_MODE_OPTIONS.map((option) => (
            <AiModeOption
              key={option.value}
              mode={option.value}
              label={option.label}
              description={option.description}
              selected={aiMode === option.value}
              danger={option.danger}
              onSelect={setAiMode}
            />
          ))}
        </div>
      </Card>

      <Card>
        <h2 className="mb-1 font-semibold text-slate-700">Default currency</h2>
        <p className="mb-3 text-sm text-slate-500">
          Pre-selected when you create accounts or loans. Each account can still use its own currency.
        </p>
        <Select
          className="max-w-xs"
          value={user?.defaultCurrency ?? "PKR"}
          onChange={(e) => void saveCurrency(e.target.value)}
        >
          {CURRENCIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </Select>
      </Card>
    </div>
  );
}

function AiModeOption({
  mode,
  label,
  description,
  selected,
  danger,
  onSelect,
}: {
  mode: AiMode;
  label: string;
  description: string;
  selected: boolean;
  danger?: boolean;
  onSelect: (mode: AiMode) => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={() => onSelect(mode)}
      className={`flex w-full items-start gap-3 rounded-xl border px-3 py-3 text-left transition ${
        selected
          ? danger
            ? "border-red-300 bg-red-50 text-red-900"
            : "border-brand-200 bg-brand-50 text-brand-900"
          : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50"
      }`}
    >
      {selected ? (
        <CheckCircle size={19} className={danger ? "mt-0.5 shrink-0 text-red-600" : "mt-0.5 shrink-0 text-brand-500"} />
      ) : (
        <Circle size={19} className="mt-0.5 shrink-0 text-slate-300" />
      )}
      <span>
        <span className="block text-sm font-semibold">{label}</span>
        <span className={selected ? "mt-0.5 block text-xs opacity-80" : "mt-0.5 block text-xs text-slate-500"}>
          {description}
        </span>
      </span>
    </button>
  );
}

function CategoriesTab(): JSX.Element {
  const { data: categories, isLoading } = useCategories();
  const createCategory = useCreateCategory();
  const deleteCategory = useDeleteCategory();
  const [kind, setKind] = useState<CategoryKind>("expense");
  const [name, setName] = useState("");

  const addCategory = async (): Promise<void> => {
    if (!name.trim()) return;
    try {
      await createCategory.mutateAsync({ kind, name: name.trim() });
      setName("");
      toast.success("Category added");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to add");
    }
  };

  const removeCategory = async (id: string, label: string): Promise<void> => {
    const ok = await confirm({ title: `Delete "${label}"?`, confirmLabel: "Delete", danger: true });
    if (!ok) return;
    await deleteCategory.mutateAsync(id).then(() => toast.success("Deleted"));
  };

  if (isLoading) return <Spinner />;
  const income = (categories ?? []).filter((c) => c.kind === "income");
  const expense = (categories ?? []).filter((c) => c.kind === "expense");

  return (
    <Card>
      <h2 className="mb-1 font-semibold text-slate-700">Income &amp; expense types</h2>
      <p className="mb-3 text-sm text-slate-500">
        These categories appear in the Income/Expense modals. System defaults can't be deleted.
      </p>

      <div className="mb-4 flex flex-wrap items-end gap-2">
        <Select className="w-32" label="Kind" value={kind} onChange={(e) => setKind(e.target.value as CategoryKind)}>
          <option value="expense">Expense</option>
          <option value="income">Income</option>
        </Select>
        <Input className="flex-1" label="New category" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Subscriptions" />
        <Button onClick={() => void addCategory()} disabled={createCategory.isPending || !name.trim()}>
          <Plus size={16} /> Add
        </Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <CategoryList title="Income" items={income} onDelete={removeCategory} />
        <CategoryList title="Expense" items={expense} onDelete={removeCategory} />
      </div>
    </Card>
  );
}

function CategoryList({
  title,
  items,
  onDelete,
}: {
  title: string;
  items: Array<{ id: string; name: string; userId: string | null }>;
  onDelete: (id: string, label: string) => void;
}): JSX.Element {
  return (
    <div>
      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">{title}</p>
      <ul className="divide-y divide-slate-100 rounded-lg border border-slate-100">
        {items.map((c) => (
          <li key={c.id} className="flex items-center justify-between px-3 py-2">
            <span className="text-sm text-slate-700">{c.name}</span>
            {c.userId ? (
              <button onClick={() => onDelete(c.id, c.name)} className="text-slate-300 hover:text-red-500">
                <Trash2 size={15} />
              </button>
            ) : (
              <span className="text-[10px] uppercase text-slate-300">default</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
